import {
  cloneElement,
  isValidElement,
  useEffect,
  useId,
  useRef,
  useState,
  type FormEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import { ClipboardCheck, History, Plus, RefreshCw, Settings2, X } from 'lucide-react';
import type { State } from '../shared/model';
import type {
  AutomationConfigurationState,
  AutomationSummary,
  AutomationTest,
  AutomationTestResult,
  CampaignAutomations,
} from '../shared/automations';
import './automations.css';

const configurationLabels: Record<AutomationConfigurationState, string> = {
  desligada: 'Desligada',
  ativa: 'Ativa',
  desconhecida: 'Desconhecida',
};
const testLabels: Record<AutomationTestResult, string> = {
  pendente: 'Teste pendente',
  aprovado: 'Teste aprovado',
  reprovado: 'Teste reprovado',
};
const dayLabel = (value: string) =>
  new Date(`${value}T12:00:00Z`).toLocaleDateString('pt-BR', { timeZone: 'UTC' });
const stamp = (value: string) =>
  new Date(value).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' });
const field = (form: FormData, name: string) => String(form.get(name) ?? '').trim();
type Editor =
  { kind: 'configuration'; item?: AutomationSummary } | { kind: 'test'; item: AutomationSummary };
type Props = {
  campaignId: string;
  data: State;
  refresh: () => Promise<unknown>;
  initialAutomationId?: string;
};
function Field({
  label,
  children,
  wide = false,
}: {
  label: string;
  children: ReactNode;
  wide?: boolean;
}) {
  const id = useId();
  return (
    <label className={`field ${wide ? 'wide' : ''}`}>
      <span id={id}>{label}</span>
      {isValidElement(children)
        ? cloneElement(children as ReactElement<{ 'aria-labelledby'?: string }>, {
            'aria-labelledby': id,
          })
        : children}
    </label>
  );
}
function Modal({
  children,
  title,
  busy,
  onClose,
}: {
  children: ReactNode;
  title: string;
  busy: boolean;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null),
    id = useId();
  useEffect(() => {
    ref.current?.showModal();
    return () => ref.current?.close();
  }, []);
  return (
    <dialog
      ref={ref}
      className="automation-dialog"
      aria-labelledby={id}
      onCancel={(event) => {
        event.preventDefault();
        if (!busy) onClose();
      }}
    >
      <header>
        <h2 id={id}>{title}</h2>
        <button
          type="button"
          aria-label="Fechar formulário de automação"
          disabled={busy}
          onClick={onClose}
        >
          <X size={18} />
        </button>
      </header>
      {children}
    </dialog>
  );
}
function TestDetails({ test }: { test: AutomationTest }) {
  return (
    <div className="automation-test-details">
      <p>
        <strong>{testLabels[test.result]}</strong> nos{' '}
        {test.cases.length === 1 ? 'casos registrados' : `${test.cases.length} casos registrados`} ·{' '}
        {dayLabel(test.testedOn)}
      </p>
      <p>
        Conta usada no teste: <strong>{test.testAccount}</strong>
      </p>
      <ul>
        {test.cases.map((item, index) => (
          <li key={index}>
            <strong>{item.name}</strong>
            <span className={`automation-badge ${item.result}`}>
              {item.result === 'aprovado' ? 'Passou' : 'Falhou'}
            </span>
            <p>{item.evidence}</p>
          </li>
        ))}
      </ul>
      <p>
        <strong>Fonte:</strong> {test.source}
      </p>
      <p className="automation-prewrap">{test.evidence}</p>
      <p className="automation-fine">
        Registrado por {test.recordedBy} em {stamp(test.createdAt)} · São Paulo.
      </p>
    </div>
  );
}
function TestFields({ item, today }: { item: AutomationSummary; today: string }) {
  const [cases, setCases] = useState([0]);
  const next = useRef(1);
  return (
    <>
      <p className="automation-note wide">
        {item.name} · configuração {item.configurationRevision}. Registre somente os casos
        executados e a evidência de cada resultado. Este formulário não executa o teste.
      </p>
      <Field label="Data do teste">
        <input name="testedOn" type="date" required max={today} defaultValue={today} />
      </Field>
      <Field label="Conta usada no teste">
        <input
          name="testAccount"
          required
          maxLength={250}
          placeholder="Perfil de fora usado para conferir a resposta"
        />
      </Field>
      <div className="automation-cases wide">
        <h3>Casos testados</h3>
        {cases.map((id, index) => (
          <fieldset key={id}>
            <legend>Caso {index + 1}</legend>
            <input type="hidden" name="caseIds" value={id} />
            <Field label={`Caso ${index + 1}: o que foi testado`}>
              <input
                name={`caseName:${id}`}
                required
                minLength={3}
                maxLength={250}
                placeholder="Ex.: GUIA como primeira mensagem"
              />
            </Field>
            <Field label={`Caso ${index + 1}: resultado`}>
              <select name={`caseResult:${id}`} required defaultValue="">
                <option value="">Selecione o resultado observado</option>
                <option value="aprovado">Passou</option>
                <option value="reprovado">Falhou</option>
              </select>
            </Field>
            <Field label={`Caso ${index + 1}: evidência`}>
              <textarea
                name={`caseEvidence:${id}`}
                required
                minLength={3}
                maxLength={12000}
                rows={2}
                placeholder="O que aconteceu e onde a comprovação está preservada"
              />
            </Field>
            {cases.length > 1 && (
              <button
                type="button"
                onClick={() => setCases((items) => items.filter((value) => value !== id))}
              >
                Remover caso {index + 1}
              </button>
            )}
          </fieldset>
        ))}
        {cases.length < 30 && (
          <button
            type="button"
            onClick={() => {
              const id = next.current++;
              setCases((items) => [...items, id]);
            }}
          >
            <Plus size={14} />
            Adicionar caso
          </button>
        )}
      </div>
      <Field label="Fonte do teste" wide>
        <input
          name="source"
          required
          minLength={3}
          maxLength={12000}
          placeholder="Ex.: conferência manual com uma conta de fora"
        />
      </Field>
      <Field label="Evidência geral do teste" wide>
        <textarea
          name="evidence"
          required
          minLength={3}
          maxLength={12000}
          rows={3}
          placeholder="Referência às capturas e observações da execução"
        />
      </Field>
      <p className="automation-fine wide">
        Se qualquer caso falhar, este teste ficará reprovado. Aprovação cobre apenas os casos
        registrados. Um novo teste preserva os anteriores.
      </p>
    </>
  );
}

export function AutomationWorkspace({ campaignId, data, refresh, initialAutomationId }: Props) {
  const [workspace, setWorkspace] = useState<CampaignAutomations>();
  const [editor, setEditor] = useState<Editor>();
  const [error, setError] = useState(''),
    [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(true),
    [busy, setBusy] = useState(false);
  const sequence = useRef(0),
    focused = useRef<string | undefined>(undefined);
  const endpoint = `/api/campaigns/${encodeURIComponent(campaignId)}/automations`;
  const canEdit = data.permissions.edit;
  async function load() {
    const request = ++sequence.current;
    const response = await fetch(endpoint),
      result = await response.json();
    if (!response.ok) throw new Error(result.error || 'Não foi possível consultar as automações.');
    if (request === sequence.current) setWorkspace(result);
  }
  useEffect(() => {
    setWorkspace(undefined);
    setEditor(undefined);
    setError('');
    setNotice('');
    setLoading(true);
    focused.current = undefined;
    void load()
      .catch((reason) => setError(reason.message))
      .finally(() => setLoading(false));
    return () => {
      sequence.current++;
    };
  }, [campaignId]);
  useEffect(() => {
    if (!initialAutomationId) {
      focused.current = undefined;
      return;
    }
    if (
      focused.current !== initialAutomationId &&
      workspace?.automations.some((item) => item.id === initialAutomationId)
    ) {
      focused.current = initialAutomationId;
      const target = document.getElementById(`automation-${initialAutomationId}`);
      target?.scrollIntoView({ block: 'center' });
      target?.focus({ preventScroll: true });
    }
  }, [initialAutomationId, workspace]);
  async function reload() {
    setLoading(true);
    setError('');
    try {
      await load();
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setLoading(false);
    }
  }
  function open(value: Editor) {
    setEditor(value);
    setError('');
    setNotice('');
  }
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!editor || busy) return;
    const form = new FormData(event.currentTarget);
    let body: unknown,
      path = endpoint,
      method = 'POST';
    if (editor.kind === 'configuration') {
      body = {
        name: field(form, 'name'),
        channel: field(form, 'channel'),
        account: field(form, 'account'),
        triggers: field(form, 'triggers')
          .split('\n')
          .map((value) => value.trim())
          .filter(Boolean),
        responseSummary: field(form, 'responseSummary'),
        configurationState: field(form, 'configurationState'),
        source: field(form, 'source'),
        checkedOn: field(form, 'checkedOn'),
        owner: field(form, 'owner'),
        ...(editor.item ? { version: editor.item.version } : {}),
      };
      if (editor.item) {
        path += `/${encodeURIComponent(editor.item.id)}`;
        method = 'PUT';
      }
    } else {
      path += `/${encodeURIComponent(editor.item.id)}/tests`;
      body = {
        automationVersion: editor.item.version,
        configurationRevision: editor.item.configurationRevision,
        cases: form.getAll('caseIds').map((id) => ({
          name: field(form, `caseName:${id}`),
          result: field(form, `caseResult:${id}`),
          evidence: field(form, `caseEvidence:${id}`),
        })),
        testedOn: field(form, 'testedOn'),
        testAccount: field(form, 'testAccount'),
        source: field(form, 'source'),
        evidence: field(form, 'evidence'),
      };
    }
    setBusy(true);
    setError('');
    setNotice('');
    let saved = false;
    try {
      const response = await fetch(path, {
        method,
        headers: { 'Content-Type': 'application/json', 'X-FLG-CSRF': data.csrf },
        body: JSON.stringify(body),
      });
      const result = await response.json();
      if (!response.ok) {
        if (response.status === 409) {
          setEditor(undefined);
          await load();
        }
        throw new Error(result.error || 'Não foi possível salvar o registro.');
      }
      saved = true;
      setEditor(undefined);
      setNotice(
        editor.kind === 'test'
          ? 'Resultado registrado para esta versão da configuração.'
          : 'Configuração observada registrada no FLG.',
      );
      await Promise.all([load(), refresh()]);
    } catch (reason) {
      setError(
        saved
          ? 'O registro foi salvo, mas a atualização da tela falhou. Atualize para consultar o estado atual.'
          : (reason as Error).message,
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="automation-workspace" aria-labelledby="automation-heading">
      <header className="automation-heading">
        <div>
          <h2 id="automation-heading">Automações da campanha</h2>
          <p>Configurações observadas e testes registrados pela equipe.</p>
        </div>
        <div className="automation-actions">
          <button disabled={busy || loading} onClick={() => void reload()}>
            <RefreshCw size={14} className={loading ? 'spin' : ''} />
            Atualizar automações
          </button>
          {canEdit && (
            <button
              className="primary"
              disabled={busy}
              onClick={() => open({ kind: 'configuration' })}
            >
              <Plus size={15} />
              Cadastrar automação
            </button>
          )}
        </div>
      </header>
      <p className="automation-note">
        Acompanhamento manual. Salvar aqui registra o que foi conferido; não ativa automações nem
        envia mensagens na Meta. Configuração ativa e teste aprovado são informações separadas.
      </p>
      {!editor && error && (
        <p className="notice error" role="alert">
          {error}
        </p>
      )}
      {notice && (
        <p className="notice" role="status">
          {notice}
        </p>
      )}
      {!workspace ? (
        <p role="status">
          {loading
            ? 'Carregando automações…'
            : 'Automações ainda não disponíveis. Atualize para tentar novamente.'}
        </p>
      ) : !workspace.automations.length ? (
        <div className="automation-empty">
          <Settings2 size={25} />
          <h3>Nenhuma automação cadastrada</h3>
          <p>
            Registre a conta, os gatilhos e a resposta observada. O teste começa pendente até que a
            equipe registre sua execução.
          </p>
        </div>
      ) : (
        <div className="automation-list">
          {workspace.automations.map((item) => {
            const current = workspace.tests.find((test) => test.id === item.currentTestId);
            const revisions = workspace.revisions.filter((value) => value.automationId === item.id);
            const tests = workspace.tests.filter((value) => value.automationId === item.id);
            return (
              <article
                className="panel automation-card"
                id={`automation-${item.id}`}
                tabIndex={-1}
                key={item.id}
              >
                <header>
                  <div>
                    <span className="automation-kicker">
                      {item.channel} · {item.account}
                    </span>
                    <h3>{item.name}</h3>
                  </div>
                  <div className="automation-states">
                    <span className={`automation-badge ${item.configurationState}`}>
                      Configuração {configurationLabels[item.configurationState].toLowerCase()}
                    </span>
                    <span className={`automation-badge ${item.testResult}`}>
                      {testLabels[item.testResult]}
                    </span>
                  </div>
                </header>
                <p className="automation-fine">
                  Configuração {item.configurationRevision} · conferida em{' '}
                  {dayLabel(item.checkedOn)} · responsável: {item.owner || 'a definir'}.
                </p>
                {item.configurationState === 'ativa' && item.testResult !== 'aprovado' && (
                  <p className="automation-attention">
                    {item.testResult === 'reprovado'
                      ? 'A configuração consta ativa, mas o teste vigente falhou. Confira a ocorrência antes de confiar na resposta.'
                      : 'A configuração consta ativa e ainda precisa de teste desta versão.'}
                  </p>
                )}
                <dl>
                  <div>
                    <dt>Gatilhos</dt>
                    <dd>{item.triggers.join(' · ')}</dd>
                  </div>
                  <div>
                    <dt>Resumo da resposta</dt>
                    <dd className="automation-prewrap">{item.responseSummary}</dd>
                  </div>
                  <div>
                    <dt>Origem da conferência</dt>
                    <dd className="automation-prewrap">{item.source}</dd>
                  </div>
                </dl>
                {canEdit && (
                  <div className="automation-actions">
                    <button onClick={() => open({ kind: 'configuration', item })}>
                      <Settings2 size={14} />
                      Revisar configuração
                    </button>
                    <button className="primary" onClick={() => open({ kind: 'test', item })}>
                      <ClipboardCheck size={14} />
                      Registrar teste
                    </button>
                  </div>
                )}
                {current && (
                  <details className="automation-current">
                    <summary>Teste vigente · {dayLabel(current.testedOn)}</summary>
                    <TestDetails test={current} />
                  </details>
                )}
                <details className="automation-history">
                  <summary>
                    <History size={14} />
                    Histórico de configurações e testes
                  </summary>
                  <p className="automation-fine">
                    O teste vigente é o mais recente pela data da execução na configuração atual.
                    Testes anteriores permanecem preservados e não aprovam uma revisão nova.
                  </p>
                  {revisions.map((revision) => (
                    <section key={revision.id}>
                      <h4>
                        Configuração {revision.revision}
                        {revision.revision === item.configurationRevision
                          ? ' · atual'
                          : ' · histórica'}
                      </h4>
                      <p>
                        {configurationLabels[revision.configurationState]} · {revision.channel} ·{' '}
                        {revision.account} · conferida em {dayLabel(revision.checkedOn)}
                      </p>
                      <details>
                        <summary>Consultar configuração registrada</summary>
                        <p>
                          <strong>{revision.name}</strong>
                        </p>
                        <p>Gatilhos: {revision.triggers.join(' · ')}</p>
                        <p className="automation-prewrap">{revision.responseSummary}</p>
                        <p>Fonte: {revision.source}</p>
                        <p className="automation-fine">
                          {revision.recordedBy} · {stamp(revision.createdAt)} · São Paulo.
                        </p>
                      </details>
                      {tests
                        .filter((test) => test.configurationRevision === revision.revision)
                        .map((test) => (
                          <details key={test.id}>
                            <summary>
                              {testLabels[test.result]} · {dayLabel(test.testedOn)}
                              {test.id === item.currentTestId ? ' · vigente' : ' · histórico'}
                            </summary>
                            <TestDetails test={test} />
                          </details>
                        ))}
                      {!tests.some((test) => test.configurationRevision === revision.revision) && (
                        <p className="automation-fine">
                          Nenhum teste registrado nesta configuração.
                        </p>
                      )}
                    </section>
                  ))}
                </details>
              </article>
            );
          })}
        </div>
      )}
      {editor && (
        <Modal
          title={
            editor.kind === 'test'
              ? 'Registrar resultado de teste'
              : editor.item
                ? 'Revisar configuração observada'
                : 'Cadastrar automação observada'
          }
          busy={busy}
          onClose={() => setEditor(undefined)}
        >
          <form onSubmit={submit} key={`${editor.kind}:${editor.item?.id ?? 'new'}`}>
            {editor.kind === 'configuration' ? (
              <>
                {editor.item && (
                  <p className="automation-note wide">
                    Qualquer alteração deste cadastro gera uma nova versão com teste pendente. A
                    configuração e os testes anteriores ficam no histórico.
                  </p>
                )}
                <Field label="Nome da automação" wide>
                  <input
                    name="name"
                    required
                    maxLength={250}
                    defaultValue={editor.item?.name}
                    placeholder="Ex.: GUIA no direct"
                  />
                </Field>
                <Field label="Canal">
                  <input
                    name="channel"
                    required
                    maxLength={250}
                    defaultValue={editor.item?.channel}
                    placeholder="Ex.: Instagram"
                  />
                </Field>
                <Field label="Conta">
                  <input
                    name="account"
                    required
                    maxLength={250}
                    defaultValue={editor.item?.account}
                    placeholder="@perfil ou nome da página"
                  />
                </Field>
                <Field label="Estado observado da configuração">
                  <select
                    name="configurationState"
                    defaultValue={editor.item?.configurationState ?? 'desconhecida'}
                  >
                    {Object.entries(configurationLabels).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Data da conferência">
                  <input
                    name="checkedOn"
                    type="date"
                    required
                    max={data.today}
                    defaultValue={editor.item?.checkedOn ?? data.today}
                  />
                </Field>
                <Field label="Gatilhos, um por linha" wide>
                  <textarea
                    name="triggers"
                    required
                    rows={3}
                    maxLength={10040}
                    defaultValue={editor.item?.triggers.join('\n')}
                    placeholder={'GUIA\nguia\nQuero o guia'}
                  />
                </Field>
                <Field label="Resumo da resposta" wide>
                  <textarea
                    name="responseSummary"
                    required
                    minLength={3}
                    maxLength={12000}
                    rows={3}
                    defaultValue={editor.item?.responseSummary}
                  />
                </Field>
                <Field label="Origem e evidência da conferência" wide>
                  <textarea
                    name="source"
                    required
                    minLength={3}
                    maxLength={12000}
                    rows={3}
                    defaultValue={editor.item?.source}
                    placeholder="Onde a configuração foi conferida; referência ao registro ou captura"
                  />
                </Field>
                <Field label="Responsável pelo acompanhamento" wide>
                  <input
                    name="owner"
                    maxLength={250}
                    defaultValue={editor.item?.owner}
                    placeholder="A definir"
                  />
                </Field>
              </>
            ) : (
              <TestFields item={editor.item} today={data.today} />
            )}
            {error && (
              <p className="notice error wide" role="alert">
                {error}
              </p>
            )}
            <footer className="automation-form-actions wide">
              <button type="button" disabled={busy} onClick={() => setEditor(undefined)}>
                Cancelar
              </button>
              <button type="submit" className="primary" disabled={busy}>
                {busy
                  ? 'Salvando…'
                  : editor.kind === 'test'
                    ? 'Salvar resultado do teste'
                    : 'Salvar configuração observada'}
              </button>
            </footer>
          </form>
        </Modal>
      )}
    </section>
  );
}
